import { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Upload, AlertCircle, CheckCircle } from "lucide-react";
import { useGetMyAdmissionCardQuery } from "../api/examsApi";
import { useUploadProfilePhotoMutation } from "../../auth/api/authApi";
import ExamAdmissionCard from "../components/ExamAdmissionCard";

export default function ExamAdmissionCardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetMyAdmissionCardQuery(id!);
  const [uploadPhoto, { isLoading: uploading }] = useUploadProfilePhotoMutation();
  const [uploadMsg, setUploadMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const hasPhoto = !!data?.applicant?.photo_url;

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadMsg(null);
    try {
      const fd = new FormData();
      fd.append("pro_pic", file);
      await uploadPhoto(fd).unwrap();
      setUploadMsg({ type: "success", text: "Photo uploaded successfully. Please wait a moment then refresh." });
      setTimeout(() => refetch(), 1500);
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        err?.data?.errors?.pro_pic?.[0] ||
        err?.error ||
        "Upload failed. Please try again.";
      setUploadMsg({ type: "error", text: msg });
    }
  };

  const handlePrint = () => window.print();

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; background: white; }
          #exam-admission-card {
            position: fixed; top: 0; left: 0; width: 210mm;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
        {/* Top bar */}
        <div className="no-print flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-slate-200 transition-all"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-widest">
                Admission Card
              </h1>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                Professional Examinations
              </p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            disabled={isLoading || isError || !hasPhoto}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all disabled:opacity-50"
          >
            <Printer className="w-4 h-4" />
            Print Card
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        )}

        {isError && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center">
            <p className="text-rose-700 font-black text-sm uppercase tracking-widest">
              Unable to load admission card.
            </p>
            <p className="text-rose-500 text-xs mt-1">
              Your application may still be under review, or a timetable has not been published yet.
            </p>
          </div>
        )}

        {/* Missing photo banner */}
        {data && !hasPhoto && (
          <div className="no-print bg-amber-50 border border-amber-200 rounded-2xl p-6 space-y-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-black text-amber-800 uppercase tracking-widest">
                  Passport Photo Required
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Your admission card cannot be printed without a passport photograph. Upload one below to proceed.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={handlePhotoUpload}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-amber-700 transition-all disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                {uploading ? "Uploading..." : "Upload Passport Photo"}
              </button>
              {uploadMsg && (
                <div className={`flex items-center gap-2 text-xs font-bold ${uploadMsg.type === "success" ? "text-emerald-700" : "text-rose-600"}`}>
                  {uploadMsg.type === "success"
                    ? <CheckCircle className="w-4 h-4" />
                    : <AlertCircle className="w-4 h-4" />}
                  {uploadMsg.text}
                </div>
              )}
            </div>
          </div>
        )}

        {data && (
          <div className="flex justify-center">
            <div className="shadow-2xl">
              <ExamAdmissionCard data={data} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
